import React from 'react';
import { Image } from '@react-pdf/renderer';

const normalizeBase64Payload = (value: string): string | undefined => {
  const sanitized = value.trim().replaceAll(/\s+/g, '').replaceAll('-', '+').replaceAll('_', '/');

  if (!sanitized) {
    return undefined;
  }

  const remainder = sanitized.length % 4;
  if (remainder === 1) {
    return undefined;
  }

  const padded = remainder === 0 ? sanitized : sanitized.padEnd(sanitized.length + (4 - remainder), '=');
  return /^[A-Za-z0-9+/]+={0,2}$/.test(padded) ? padded : undefined;
};

const JPEG_DATA_URI_PREFIX = 'data:image/jpeg;base64,';
const MIN_JPEG_BASE64_PAYLOAD_LENGTH = 8;

const isRenderableJpegDataUri = (value: string | undefined): value is string =>
  typeof value === 'string' &&
  value.startsWith(JPEG_DATA_URI_PREFIX) &&
  value.length > JPEG_DATA_URI_PREFIX.length + MIN_JPEG_BASE64_PAYLOAD_LENGTH;

function svgToDataURIDynamic(
  svgBase64: string,
  shiftCanvas?: { cx: number; cy: number; dx: number; dy: number },
): Promise<string | undefined> {
  const imgSource = `data:image/svg+xml;base64,${svgBase64}`;
  const canvas = document.createElement('canvas');
  const img = document.createElement('img');
  canvas.setAttribute('style', 'display: none');
  document.body.appendChild(canvas);
  img.setAttribute('src', imgSource);

  return new Promise((res) => {
    const removeCanvas = () => canvas.remove();
    img.onload = function load() {
      try {
        const sourceWidth = img.width || img.naturalWidth;
        const sourceHeight = img.height || img.naturalHeight;
        if (sourceWidth <= 0 || sourceHeight <= 0) {
          removeCanvas();
          res(undefined);
          return;
        }
        canvas.width = Math.max(1, sourceWidth + (shiftCanvas?.cx ?? 0));
        canvas.height = Math.max(1, sourceHeight + (shiftCanvas?.cy ?? 0));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          removeCanvas();
          res(undefined);
          return;
        }
        ctx.drawImage(
          img,
          0,
          0,
          sourceWidth,
          sourceHeight,
          shiftCanvas?.dx ?? 0,
          shiftCanvas?.dy ?? 0,
          sourceWidth,
          sourceHeight,
        );
        const url = canvas.toDataURL('image/jpeg', 1);
        removeCanvas();
        res(url);
      } catch {
        removeCanvas();
        res(undefined);
      }
    };
    img.onerror = function error() {
      removeCanvas();
      res(undefined);
    };
  });
}

export async function eprelBase64ToImageUri(eprelBase64: string): Promise<string | undefined> {
  const normalizedEprelBase64 = normalizeBase64Payload(eprelBase64 ?? '');

  if (!normalizedEprelBase64) {
    return undefined;
  }

  try {
    const uri = await svgToDataURIDynamic(normalizedEprelBase64, { cx: -11, cy: -1, dx: -6, dy: 0 });
    return isRenderableJpegDataUri(uri) ? uri : undefined;
  } catch {
    return undefined;
  }
}

export const EprelPicture = ({ eprelImageUri }: { eprelImageUri: string }) => (
  <Image src={eprelImageUri} style={{ width: '65px', height: '96px', margin: '5px 0 5px 0' }} />
);
